// tailwind
import 'src/styles/tailwind.css';

// scroll bar
import 'simplebar/src/simplebar.css';

// lazy image
import 'react-lazy-load-image-component/src/effects/black-and-white.css';
import 'react-lazy-load-image-component/src/effects/blur.css';
import 'react-lazy-load-image-component/src/effects/opacity.css';

import { Auth0ContextInterface, Auth0Provider } from '@auth0/auth0-react';
import ReactDOM from 'react-dom';
import { HelmetProvider } from 'react-helmet-async';
import { ReactLocation, Router } from 'react-location';
import { ReactLocationDevtools } from 'react-location-devtools';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { ReplaySubject } from 'rxjs';
// contexts
import { CollapseDrawerProvider } from './contexts/CollapseDrawerContext';
import { SettingsProvider } from './contexts/SettingsContext';

//
import { config } from 'src/config';
import App from './App';
import { RequireAuth } from './auth';
import { AuthenticationStatusNotifier } from './auth/AuthenticationStatusNotifier';
import { setTokenGenerator } from './axios';
import { LocationGenerics, routes } from './routes';

import { datadogRum } from '@datadog/browser-rum';
import * as Sentry from '@sentry/browser';
import LogRocket from 'logrocket';
import { IntercomProvider } from 'react-use-intercom';
import { identifyUser } from './analytics';
import { initErrorReporting } from './errorReporting';

// ----------------------------------------------------------------------

const INTERCOM_APP_ID = config.intercom.appId;

const queryClient = new QueryClient();
const location = new ReactLocation<LocationGenerics>();

// Sentry and LogRocket Error reporting
initErrorReporting();

// Segment analytics
if (process.env.NODE_ENV === 'production') {
  window.analytics.load(config.segment.writeKey);
}

// Datadog RUM
if (process.env.NODE_ENV === 'production' && config.datadog.applicationId) {
  datadogRum.init(config.datadog);
  datadogRum.startSessionReplayRecording();
}

// Fire new page view event for Segment Analytics on page change
let previousPath = '';
location.subscribe(() => {
  const currentPath = location.current.pathname;
  if (currentPath !== previousPath) {
    previousPath = currentPath;
    window.analytics.page();
  }
});

// Used to notify non-react components that the user is authenticated.
// Using ReplaySubject because sometimes the router loads after the user is
// authenticated and it still needs to receive the event.
const authenticationStatusSubject = new ReplaySubject<boolean>();

const handleAuthenticated = async (
  accessTokenGenerator: Auth0ContextInterface['getAccessTokenSilently'],
  user: Auth0ContextInterface['user']
) => {
  setTokenGenerator(accessTokenGenerator);
  console.log('handleAuthenticated');
  authenticationStatusSubject.next(true);

  // Tell Sentry the active user so it can associate errors with the user.
  if (user && user.sub && user.email) {
    // Intercom identifies the user in the App.tsx component because it requires the use of React hooks.
    identifyUser(user.sub, user.email);
    Sentry.setUser({ id: user.sub, email: user.email });
    LogRocket.identify(user.sub, { email: user.email });
  }
};

ReactDOM.render(
  <HelmetProvider>
    {/* To see auth status inside of React components, use the 'useAuth0' hook */}
    <Auth0Provider
      domain={config.auth0.domain}
      clientId={config.auth0.clientId}
      audience={config.auth0.audience}
      redirectUri={window.location.origin}
      // onRedirectCallback={onRedirectCallback}
    >
      <AuthenticationStatusNotifier onAuthenticated={handleAuthenticated} />
      <QueryClientProvider client={queryClient}>
        {process.env.NODE_ENV !== 'production' && (
          <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
        )}
        <SettingsProvider>
          <CollapseDrawerProvider>
            <Router location={location} routes={routes}>
              {process.env.NODE_ENV !== 'production' && (
                <ReactLocationDevtools initialIsOpen={false} />
              )}
              {/* The app requires authentication for every route (if not
            authenticated, it redirects to Auth0 login portal) */}
              <RequireAuth>
                <IntercomProvider appId={INTERCOM_APP_ID}>
                  <App />
                </IntercomProvider>
              </RequireAuth>
            </Router>
          </CollapseDrawerProvider>
        </SettingsProvider>
      </QueryClientProvider>
    </Auth0Provider>
  </HelmetProvider>,
  document.getElementById('root')
);

export { queryClient, authenticationStatusSubject };
